import { useEffect, useMemo, useState, useRef } from "react";
import { Search, ChevronLeft, ChevronRight, Check, Trash2, Eye } from "lucide-react";

export default function DataTable({
  data,
  columns,
  onView,
  onEdit,
  canDeleteRow,
  onDelete,
  onBulkDelete,
  searchPlaceholder = "Cari...",
  itemsPerPage = 10,
  loading = false,

  // Controlled pagination props
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,

  // ✅ Server-mode props
  serverMode = false,
  serverTotal = 0,
  serverTotalPages = 1,

  // ✅ Search control/notify
  onSearchChange,
  searchValue, // optional controlled search value from parent
  searchDebounceMs = 250,
}) {
  // ✅ controlled OR uncontrolled
  const [localSearch, setLocalSearch] = useState("");
  const isControlled = typeof searchValue === "string";
  const searchTerm = isControlled ? searchValue : localSearch;
  const canDelete = (row) => (typeof canDeleteRow === "function" ? canDeleteRow(row) : true);

  const [selectedItems, setSelectedItems] = useState(new Set());

  // ---------------- Controlled Pagination ----------------
  const currentPage = page ?? 1;

  const effectivePageSize =
    pageSize === "all"
      ? data.length === 0
        ? 1
        : data.length
      : pageSize ?? itemsPerPage;

  // ---------------- Server search debounce ----------------
  // We debounce ONLY serverMode requests.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!serverMode) return;
    if (typeof onSearchChange !== "function") return;

    // debounce backend query
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(searchTerm);
    }, searchDebounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [serverMode, searchTerm, onSearchChange, searchDebounceMs]);

  // ---------------- Filter data (client mode) ----------------
  const filteredData = useMemo(() => {
    if (serverMode) return data; // server already filtered
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data;

    return data.filter((item) => {
      const searchString = Object.values(item).join(" ").toLowerCase();
      return searchString.includes(term);
    });
  }, [data, searchTerm, serverMode]);

  // ---------------- Total pages ----------------
  const totalPages = useMemo(() => {
    if (serverMode) return Math.max(1, Number(serverTotalPages) || 1);
    if (pageSize === "all") return 1;
    return Math.max(1, Math.ceil(filteredData.length / effectivePageSize));
  }, [serverMode, serverTotalPages, pageSize, filteredData.length, effectivePageSize]);

  // ---------------- Start index ----------------
  const startIndex = useMemo(() => {
    if (serverMode) return 0;
    return pageSize === "all" ? 0 : (currentPage - 1) * effectivePageSize;
  }, [serverMode, pageSize, currentPage, effectivePageSize]);

  // ---------------- Paginated data ----------------
  const paginatedData = useMemo(() => {
    if (serverMode) return filteredData;
    return pageSize === "all"
      ? filteredData
      : filteredData.slice(startIndex, startIndex + effectivePageSize);
  }, [serverMode, filteredData, pageSize, startIndex, effectivePageSize]);
  const selectableRows = useMemo(
  () => paginatedData.filter((row) => canDelete(row)),
  [paginatedData, canDeleteRow]// (canDelete depends on canDeleteRow)
);
const deletableSelectedCount = useMemo(() => {
  return data.filter((item) => selectedItems.has(item.id) && canDelete(item)).length;
}, [data, selectedItems, canDelete]);

const allSelected =
  selectableRows.length > 0 &&
  selectableRows.every((row) => selectedItems.has(row.id));

  // ---------------- Footer numbers ----------------
  const footer = useMemo(() => {
    if (serverMode) {
      const total = Math.max(0, Number(serverTotal) || 0);
      const limit =
        pageSize === "all" ? total : Math.max(1, Number(pageSize) || itemsPerPage);

      const from = total === 0 ? 0 : (currentPage - 1) * limit + 1;
      const to = total === 0 ? 0 : Math.min(currentPage * limit, total);

      return { fromRow: from, toRow: to, totalRowCount: total };
    }

    const total = filteredData.length;
    const from = total === 0 ? 0 : startIndex + 1;
    const to =
      total === 0
        ? 0
        : Math.min(
            startIndex + (pageSize === "all" ? total : effectivePageSize),
            total
          );

    return { fromRow: from, toRow: to, totalRowCount: total };
  }, [
    serverMode,
    serverTotal,
    pageSize,
    itemsPerPage,
    currentPage,
    filteredData.length,
    startIndex,
    effectivePageSize,
  ]);

  // ---------------- Selection ----------------
  const toggleSelectAll = () => {
  if (!selectableRows.length) return;

  if (allSelected) {
    const next = new Set(selectedItems);
    for (const row of selectableRows) next.delete(row.id);
    setSelectedItems(next);
  } else {
    const next = new Set(selectedItems);
    for (const row of selectableRows) next.add(row.id);
    setSelectedItems(next);
  }
};


  const toggleSelectItem = (row) => {
    if (!canDelete(row)) return;
    const id = row.id;
    const newSet = new Set(selectedItems);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedItems(newSet);
  };

  const handleBulkDelete = () => {
  if (!onBulkDelete) return;

  const itemsToDelete = data.filter(
    (item) => selectedItems.has(item.id) && canDelete(item)
  );

  if (itemsToDelete.length === 0) return;

  onBulkDelete(itemsToDelete);

  // clear only those deleted from selection
  const next = new Set(selectedItems);
  for (const item of itemsToDelete) next.delete(item.id);
  setSelectedItems(next);
};

  const handleChangePageSize = (e) => {
    const value = e.target.value;
    const newSize = value === "all" ? "all" : Number(value);

    onPageSizeChange?.(newSize);
    onPageChange?.(1);
    setSelectedItems(new Set());
  };

  // ✅ IMPORTANT FIX:
  // - Always update displayed value immediately.
  // - If controlled: call onSearchChange(value) immediately (client mode),
  //   server mode will still debounce via the effect above.
  const handleChangeSearch = (value) => {
    if (!isControlled) setLocalSearch(value);

    // For CLIENT MODE, parent needs to update immediately so input is typable.
    // For SERVER MODE, we can also update immediately (harmless), but backend
    // request remains debounced by the effect.
    onSearchChange?.(value);

    onPageChange?.(1);
    setSelectedItems(new Set());
  };

  // ✅ safe early-return after hooks
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const { fromRow, toRow, totalRowCount } = footer;

  return (
    <div className="space-y-4">
      {/* Search + bulk delete */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => handleChangeSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F7CFF] focus:border-transparent transition-all"
          />
        </div>

       {deletableSelectedCount > 0 && onBulkDelete && (
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            <Trash2 className="w-4 h-4" />
           Hapus {deletableSelectedCount} item
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50/80 border-b border-gray-200">
            <tr>
              {onBulkDelete && (
                <th className="w-12 px-6 py-3 text-left">
                  <button
                    onClick={toggleSelectAll}
                    disabled={!selectableRows.length}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      allSelected
                        ? "bg-[#4F7CFF] border-[#4F7CFF]"
                        : "border-gray-300 hover:border-[#4F7CFF]"
                      } ${!selectableRows.length ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {allSelected && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                  </button>
                </th>
              )}

              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-center ${
                    col.className || ""
                  }`}
                >
                  {col.label}
                </th>
              ))}

              {(onView || onEdit || onDelete) && (
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Aksi
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (onBulkDelete ? 1 : 0) +
                    (onView || onEdit || onDelete ? 1 : 0)
                  }
                  className="px-4 py-8 text-center text-gray-500"
                >
                  Tidak ada data
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-blue-50/30 transition-colors"
                >
                  {onBulkDelete && (
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleSelectItem(item)}
                        disabled={!canDelete(item)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          selectedItems.has(item.id)
                            ? "bg-[#4F7CFF] border-[#4F7CFF]"
                            : "border-gray-300 hover:border-[#4F7CFF]"
                        } ${!canDelete(item) ? "opacity-40 cursor-not-allowed hover:border-gray-300" : ""}`}
                      >
                        {selectedItems.has(item.id) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </button>
                    </td>
                  )}

                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-6 py-4 text-sm text-gray-700 text-center ${
                        col.className || ""
                      }`}
                    >
                      {col.render ? col.render(item) : String(item[col.key] ?? "-")}
                    </td>
                  ))}

                  {(onView || onEdit || onDelete) && (
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {onView && (
                          <button
                            onClick={() => onView(item)}
                            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1.5"
                          >
                            <Eye className="w-4 h-4" />
                            Lihat
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => onEdit(item)}
                            className="px-3 py-1.5 text-sm font-medium text-[#4F7CFF] hover:text-[#3D6AEE] transition-colors"
                          >
                            Edit
                          </button>
                        )}
                        {onDelete && canDelete(item) && (
  <button
    onClick={() => onDelete(item)}
    className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
  >
    Hapus
  </button>
)}

                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {totalRowCount > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Menampilkan {fromRow}-{toRow} dari {totalRowCount} data
          </p>

          <div className="flex items-center gap-6">
            {/* Page size */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Tampilkan</span>

              <select
                value={pageSize}
                onChange={handleChangePageSize}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F7CFF]"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value="all">Semua</option>
              </select>

              <span className="text-sm text-gray-600">per halaman</span>
            </div>

            {/* Pagination */}
            {totalPages > 1 && pageSize !== "all" && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange?.(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <span className="text-sm text-gray-600">
                  Halaman {currentPage} dari {totalPages}
                </span>

                <button
                  onClick={() => onPageChange?.(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
